import React from 'react';
import { Trans } from '@lingui/react';

import { Box, Text } from '@rover/kibble/core';
import type { ApiLocationAutocompleteRetrieve200PredictionsItemStructuredFormatting } from '@rover/rsdk/src/apiClient/latest/model/apiLocationAutocompleteRetrieve200PredictionsItemStructuredFormatting';
import { captureError } from '@rover/rsdk/src/modules/ErrorReporting';

import { CURRENT_LOCATION_OPTION } from '../LocationInput.constants';
import { shouldShowCurrentLocation } from '../utils';

import OptionLabel from './OptionLabel';

type MatchedSubstring = {
  length: number;
  offset: number;
};

type Props = {
  label: string;
  inputValue?: string;
  structuredFormatting?: ApiLocationAutocompleteRetrieve200PredictionsItemStructuredFormatting;
  enableTwoLineDisplay?: boolean;
};

function renderMainText(mainText: string, matches?: MatchedSubstring[]): JSX.Element {
  if (!matches || matches.length === 0) {
    return <Text size="200">{mainText}</Text>;
  }

  const match = matches[0];
  const before = mainText.slice(0, match!.offset);
  const matchedText = mainText.slice(match.offset, match.offset + match.length);
  const after = mainText.slice(match.offset + match.length);

  return (
    <Text size="200" pb="1x">
      {before}
      <Text fontWeight="semibold">{matchedText}</Text>
      {after}
    </Text>
  );
}

export default function SuggestionOption({
  label,
  inputValue,
  structuredFormatting,
  enableTwoLineDisplay = false,
}: Props): JSX.Element {
  if (shouldShowCurrentLocation() && label === CURRENT_LOCATION_OPTION().label) {
    return (
      <OptionLabel title={label}>
        <Text size="100" fontWeight="semibold">
          <Trans id={label} />
        </Text>
      </OptionLabel>
    );
  }

  if (enableTwoLineDisplay && structuredFormatting && structuredFormatting.mainText) {
    return (
      <OptionLabel title={label}>
        <Box display="flex" flexDirection="column" data-testid="two-line-suggestion">
          {renderMainText(
            structuredFormatting.mainText,
            structuredFormatting.mainTextMatchedSubstrings as MatchedSubstring[]
          )}
          <Text size="100" textColor="tertiary">
            {structuredFormatting.secondaryText}
          </Text>
        </Box>
      </OptionLabel>
    );
  }

  if (!inputValue) {
    return <OptionLabel title={label}>{label}</OptionLabel>;
  }

  try {
    const escapedInput = inputValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    const parts: string[] = label.split(new RegExp(`(${escapedInput})`, 'gi'));

    return (
      <OptionLabel title={label}>
        {parts.map((part, index) => (
          <Text
            size="100"
            key={`${inputValue}-${index}`}
            {...(index % 2 ? { fontWeight: 'semibold' } : {})}
          >
            {part}
          </Text>
        ))}
      </OptionLabel>
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    captureError(new Error(`${e.toString()}\n${JSON.stringify(inputValue)}`));
    return <OptionLabel title={label}>{label}</OptionLabel>;
  }
}
